import {
    Badge,
    Box,
    Button,
    Divider,
    Flex,
    Heading,
    HStack,
    Image,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    SimpleGrid,
    Table,
    Tbody,
    Td,
    Text,
    Tr,
    useDisclosure,
    VStack
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchAuthNetTransactionStatus, fetchTransactionById, sendTransactionEmail } from '../../api';
import { getReadableUserAgent, hasPermission, Logo } from '../../Utils';

import { DownloadIcon, EmailIcon } from '@chakra-ui/icons';
import html2pdf from 'html2pdf.js';
import { PiInvoice, PiMoney, PiPrinter } from 'react-icons/pi';
import AccessDenied from '../../components/AccessDenied';
import RefundModal from './RefundModal';
import TransactionNotes from './TransactionNotes';
import VoidConfirmationModal from './VoidConfirmationModal'; // adjust path if needed

const statusDescriptions = {
    authorizedPendingCapture: 'Authorized - Pending Capture',
    capturedPendingSettlement: 'Captured - Pending Settlement',
    settledSuccessfully: 'Settled Successfully',
    refundPendingSettlement: 'Refund - Pending Settlement',
    refundedSettlementPending: 'Refund Initiated - Pending',
    refundSettledSuccessfully: 'Refund Settled',
    voided: 'Voided',
    declined: 'Declined',
    expired: 'Expired',
    failedReview: 'Failed Review',
    communicationError: 'Communication Error',
    generalError: 'General Error'
};


export default function TransactionDetails() {
    const { id } = useParams();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: isEmailModalOpen,
        onOpen: onEmailModalOpen,
        onClose: onEmailModalClose
    } = useDisclosure();
    const {
        isOpen: isVoidOpen,
        onOpen: onVoidOpen,
        onClose: onVoidClose
    } = useDisclosure();
    const navigate = useNavigate()
    const { isOpen: isRefundOpen, onOpen: onRefundOpen, onClose: onRefundClose } = useDisclosure();

    const [transaction, setTransaction] = useState(null);
    const [loading, setLoading] = useState(true);
    const [email, setEmail] = useState('');
    const [sending, setSending] = useState(false);
    const [statusLoading, setStatusLoading] = useState(false);



    const printRef = useRef();

    useEffect(() => {
        load();
    }, [id]);


    const load = async () => {
        const res = await fetchTransactionById(id);
        if (res.success) {
            setTransaction(res.data);
        }
        setLoading(false);
    };


    useEffect(() => {

        fetchStatus();
    }, [transaction?.transactionId]);



    const fetchStatus = async () => {
        if (!transaction?.transactionId) return;

        setStatusLoading(true);
        const statusRes = await fetchAuthNetTransactionStatus(transaction.transactionId);
        console.log("statusRes")
        console.log(statusRes)
        console.log('-----')
        if (statusRes.success) {
            const status = statusRes.data?.liveTransaction?.transaction?.transactionStatus;

            setTransaction(prev => ({
                ...prev,
                liveStatus: status || 'Unknown'
            }));
        } else {
            console.error('Status fetch failed:', statusRes.message || statusRes.error);
        }

        setStatusLoading(false);
    };


    const handlePrint = () => {
        const element = printRef.current;

        const opt = {
            margin: 0.5,
            filename: 'invoice.pdf',
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2 },
            jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
        };

        html2pdf()
            .set(opt)
            .from(element)
            .toPdf()
            .get('pdf')
            .then((pdf) => {
                window.open(pdf.output('bloburl'), '_blank'); // opens in new tab with browser print
            });
    };

    const handleDownloadPDF = () => {
        const element = printRef.current;

        const opt = {
            margin: 0.5,
            filename: `invoice_${transaction.claimNumber || 'payment'}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2 },
            jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
        };

        html2pdf().set(opt).from(element).save();
    };

    const handleSend = async () => {
        if (!email) return;
        setSending(true);
        const res = await sendTransactionEmail(transaction._id, email);
        setSending(false);
        if (res.success) {
            onEmailModalClose();
        } else {
            alert(res.error)
        }
    };


    if (loading) return <Text mt={10}>Loading...</Text>;
    if (!transaction) return <Text mt={10}>Transaction not found.</Text>;
    if (!hasPermission('viewTransactions')) {
        return <AccessDenied msg={"You don't have permission to view this page"} />
    }

    return (
        <Box>
            <HStack spacing={6} mb={6}>
                <VStack align="start">
                    <Heading size="lg">Transaction for {transaction.name}</Heading>
                    <Badge colorScheme={transaction.status === 'Success' ? 'green' : 'red'}>
                        This initial transaction was a {transaction.status}
                    </Badge>

                    <Text fontSize="sm" color="gray.500">Transaction ID: {transaction.transactionId}</Text>

                    <Text fontSize="sm" color="gray.600">
                        {statusLoading ? (
                            <b>Loading...</b>
                        ) : (
                            <>
                                {transaction.otherStatus === "Refunded" || transaction.otherStatus === "Voided" ?
                                    <Badge colorScheme={'red'}>
                                        {transaction.otherStatus}
                                    </Badge>
                                    :
                                    <>
                                        Live Status from Authorize.Net:{" "}
                                        <b>{transaction.liveStatus || 'N/A'}</b>
                                        {transaction.liveStatus && statusDescriptions[transaction.liveStatus] && (
                                            <> — <i>{statusDescriptions[transaction.liveStatus]}</i></>
                                        )}
                                    </>
                                }

                            </>
                        )}
                    </Text>


                </VStack>
            </HStack>

            <HStack spacing={4} mt={4}>

                {transaction?.arbSubscriptionId &&
                    <Button size="xs" colorScheme="orange" onClick={() => navigate(`/transaction/subscription/${transaction?.arbSubscriptionId}`)}>
                        View ARB details
                    </Button>
                }

                {hasPermission('emailTransactions') &&
                    <>
                        <Button leftIcon={<PiInvoice />} size={"xs"} onClick={onOpen} colorScheme="teal">Client Invoice</Button>

                        <Button leftIcon={<EmailIcon />} bg="gray.100" color="blue.900" variant="solid" size={"xs"} onClick={onEmailModalOpen}>Email Invoice</Button>
                    </>
                }


                {hasPermission('refundTransaction') &&
                    <>
                        {(transaction.liveStatus === 'settledSuccessfully' && transaction.otherStatus !== 'Refunded') &&
                            <Button leftIcon={<PiMoney />} size={"xs"} onClick={onRefundOpen} colorScheme="red">Refund</Button>
                        }
                        {transaction.liveStatus === 'capturedPendingSettlement' &&
                            <Button size="xs" colorScheme="red" variant="outline" onClick={onVoidOpen}>
                                Void
                            </Button>
                        }
                    </>
                }
            </HStack>


            <SimpleGrid templateColumns={{ base: '1fr', md: '3fr 1fr' }} spacing={8} p={8}>
                <Box>
                    <Box mb={8} p={6} borderWidth="1px" borderRadius="lg">
                        <Heading size="md" mb={4}>Client Info</Heading>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <Text><b>Name:</b> {transaction.name}</Text>
                            <Text><b>Email:</b> {transaction.email}</Text>
                            <Text><b>Phone:</b> {transaction.phone}</Text>
                            <Text><b>Submitted By:</b> {transaction.submittedBy}</Text>
                            <Text><b>Claim #:</b> {transaction.claimNumber}</Text>
                            <Text><b>Initials:</b> {transaction.initials}</Text>
                        </SimpleGrid>
                    </Box>

                    <Box mb={8} p={6} borderWidth="1px" borderRadius="lg">
                        <Heading size="md" mb={4}>Billing Address</Heading>
                        <Text>
                            {transaction.serviceAddress}, {transaction.serviceCity}, {transaction.serviceState} {transaction.serviceZipcode}, {transaction.serviceCountry}
                        </Text>
                    </Box>

                    <Box mb={8} p={6} borderWidth="1px" borderRadius="lg">
                        <Heading size="md" mb={4}>Payment Info</Heading>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <Text><b>Total Amount:</b> ${transaction.totalAmount}</Text>
                            <Text><b>Initial Charge:</b> ${transaction.initialCharge}</Text>
                            <Text><b>Card Last 4:</b> {transaction.cardLast4}</Text>
                            <Text><b>Exp Date:</b> {transaction.expirationDate}</Text>
                            <Text><b>Auth Code:</b> {transaction.authCode}</Text>
                            <Text><b>Payment Method:</b> {transaction.paymentMethod}</Text>
                        </SimpleGrid>

                        {transaction.status !== 'Success' && transaction.rawAuthResponse?.errors?.error?.length > 0 && (
                            <Text mt={2} color="red.500">
                                <b>Error:</b> {transaction.rawAuthResponse.errors.error[0].errorText}
                            </Text>
                        )}

                        <Text mt={2}><b>Message:</b> {transaction.message}</Text>
                    </Box>



                    <Box mb={8} p={6} borderWidth="1px" borderRadius="lg">
                        <Heading size="md" mb={4}>Device Info</Heading>
                        <Text><b>IP:</b> {transaction.ipAddress}</Text>
                        <Text><b>User Agent:</b> {getReadableUserAgent(transaction.userAgent)}</Text>
                        <Text><b>Created At:</b> {new Date(transaction.createdAt).toLocaleString()}</Text>
                    </Box>

                    <Divider />


                    <TransactionNotes transactionId={transaction._id} />



                </Box>

                <Box p={6} borderWidth="1px" borderRadius="lg">
                    <Heading size="md" mb={4}>Upcoming Charges</Heading>

                    {transaction.upcomingCharges?.length > 0 &&
                        <Text fontSize="sm" mb={4} color="gray.500">
                            This transaction is the first charge for payment plan of {transaction.upcomingCharges?.length + 1} Payments and considered the first payment
                        </Text>
                    }


                    {transaction.upcomingCharges?.length > 0 ? (
                        transaction.upcomingCharges.map((c, i) => (
                            <Box key={i} mb={3}>

                                <Text>
                                    ${c.amount} on {new Date(new Date(transaction?.createdAt).setMonth(new Date(transaction?.createdAt).getMonth() + (i + 1))).toLocaleDateString()}
                                </Text>
                            </Box>
                        ))
                    ) : (
                        <Text>No upcoming charges</Text>
                    )}

                    <Divider my={6} />

                    <Heading size="md" mb={2}>Metadata</Heading>
                    <Box>
                        {transaction?.arbSubscriptionId &&
                            <>
                                <Text><b>Customer Profile ID:</b> {transaction.customerProfileId}</Text>
                                <Text><b>Payment Profile ID:</b> {transaction.paymentProfileId}</Text>
                                <Text><b>Subscription ID:</b> {transaction.arbSubscriptionId}</Text>
                            </>
                        }

                        <Text><b>Response Code:</b> {transaction.responseCode}</Text>
                        <Text><b>AVS Result:</b> {transaction.avsResultCode}</Text>
                        <Text><b>CVV Result:</b> {transaction.cvvResultCode}</Text>
                    </Box>
                </Box>
            </SimpleGrid>






            <Modal scrollBehavior="inside" isOpen={isOpen} onClose={onClose} size="3xl">
                <ModalOverlay />
                <ModalContent bg="white" color="black">
                    <ModalHeader color="black">Client Invoice</ModalHeader>
                    <ModalCloseButton color="black" />
                    <ModalBody color="black">
                        <Box textAlign="center" mt={10}>
                            {/* Print & Make Another Payment Buttons */}
                            <HStack spacing={4} justify="center" mt={10} wrap="wrap">
                                <Button
                                    leftIcon={<DownloadIcon />}
                                    bg="gray.100"
                                    color="blue.900"
                                    variant="solid"
                                    size="md"
                                    onClick={handleDownloadPDF}
                                >
                                    Download PDF
                                </Button>

                                <Button
                                    leftIcon={<PiPrinter />}
                                    bg="gray.100"
                                    color="blue.900"
                                    variant="solid"
                                    size="md"
                                    onClick={handlePrint}
                                >
                                    Print Invoice
                                </Button>
                            </HStack>

                            <Box
                                ref={printRef}
                                mt={10}
                                textAlign="left"
                                bg="white"
                                color="black"
                                p={6}
                                borderRadius="md"
                                border="1px solid gray"
                            >
                                {/* Invoice Header */}
                                <Box mb={6}>
                                    <Flex
                                        direction={{ base: "column", md: "row" }}
                                        justify="space-between"
                                        align="center"
                                        wrap="wrap"
                                        gap={6}
                                    >
                                        <HStack align="flex-start" spacing={4}>
                                            <Image src={Logo} alt="Company Logo" w="70px" borderRadius="md" />
                                            <Box>
                                                <Heading size="sm" mb={1} color="black">
                                                    Diagnostic Laboratory Science
                                                </Heading>
                                                <Text fontSize="sm">7711 Garden Grove Blvd</Text>
                                                <Text fontSize="sm">Garden Grove, CA 92841</Text>
                                                <Text fontSize="sm">Phone: 888-882-3330</Text>
                                            </Box>
                                        </HStack>

                                        <Box align="flex-end">
                                            <Heading size="md" color="blue.900">
                                                Payment Invoice
                                            </Heading>
                                            <Text size="md" color="blue.900">
                                                Confirmation #: {transaction.transactionId}
                                            </Text>
                                        </Box>
                                    </Flex>
                                    <Divider mt={4} borderColor="gray.300" />
                                </Box>

                                {/* Client Information */}
                                <Heading size="sm" color="blue.900" mb={4}>
                                    Client Information
                                </Heading>
                                <Box p={4} bg="gray.50" borderRadius="md" color="black">
                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacingY={3} spacingX={10}>
                                        <HStack align="start">
                                            <Text fontWeight="bold" fontSize="sm">
                                                Name:
                                            </Text>
                                            <Text fontSize="sm">{transaction.name}</Text>
                                        </HStack>

                                        <HStack align="start">
                                            <Text fontWeight="bold" fontSize="sm">
                                                Phone:
                                            </Text>
                                            <Text fontSize="sm">{transaction.phone}</Text>
                                        </HStack>

                                        <HStack align="start">
                                            <Text fontWeight="bold" fontSize="sm">
                                                Email:
                                            </Text>
                                            <Text fontSize="sm">{transaction.email}</Text>
                                        </HStack>

                                        <HStack align="start">
                                            <Text fontWeight="bold" fontSize="sm">
                                                Claim #:
                                            </Text>
                                            <Text fontSize="sm">{transaction.claimNumber}</Text>
                                        </HStack>
                                    </SimpleGrid>
                                    <HStack mt={3} align="start" colSpan={2}>
                                        <Text fontWeight="bold" fontSize="sm">
                                            Address:
                                        </Text>
                                        <Text fontSize="sm">
                                            {transaction.serviceAddress}, {transaction.serviceCity},{" "}
                                            {transaction.serviceState} {transaction.serviceZipcode},{" "}
                                            {transaction.serviceCountry}
                                        </Text>
                                    </HStack>
                                </Box>

                                <Divider my={4} borderColor="gray.300" />

                                {/* Payment Details */}
                                <Heading size="sm" color="blue.900" mb={4}>
                                    Payment Details
                                </Heading>
                                <Box p={4} bg="gray.50" borderRadius="md" color="black">
                                    <Table variant="simple" size="sm">
                                        <Tbody>
                                            <Tr>
                                                <Td fontWeight="bold">Invoice Date</Td>
                                                <Td>{new Date(transaction.createdAt).toLocaleDateString()}</Td>
                                            </Tr>
                                            <Tr>
                                                <Td fontWeight="bold">Total Amount</Td>
                                                <Td>${Number(transaction.totalAmount).toFixed(2)}</Td>
                                            </Tr>
                                            <Tr>
                                                <Td fontWeight="bold">Payment Method</Td>
                                                <Td>Credit Card (**** **** **** {transaction.cardLast4})</Td>
                                            </Tr>
                                            <Tr>
                                                <Td fontWeight="bold">Initial Charge</Td>
                                                <Td>
                                                    ${Number(transaction.initialCharge).toFixed(2)} (Paid on{" "}
                                                    {new Date(transaction.createdAt).toLocaleDateString()})
                                                </Td>
                                            </Tr>
                                            <Tr>
                                                <Td fontWeight="bold">Payment Type</Td>
                                                <Td>
                                                    {transaction.upcomingCharges?.length === 1
                                                        ? "2 Payments"
                                                        : transaction.upcomingCharges?.length === 2
                                                            ? "3 Payments"
                                                            : "Payment in Full"}
                                                </Td>
                                            </Tr>
                                        </Tbody>
                                    </Table>
                                </Box>

                                <Divider my={4} borderColor="gray.300" />

                                <Text fontSize="sm" textAlign="center" color="gray.600">
                                    Thank you for your payment. If you have any questions, contact us at{" "}
                                    <b>BILLING@MYDLSLAB.COM</b> or Call <b>888-882-3330</b>.
                                </Text>
                            </Box>
                        </Box>
                    </ModalBody>
                </ModalContent>
            </Modal>





            <Modal isOpen={isEmailModalOpen} onClose={onEmailModalClose} size="md">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Send Invoice</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Input
                            placeholder="Enter email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            mb={4}
                        />
                        <HStack justify="end" spacing={4}>
                            <Button onClick={onEmailModalClose}>Cancel</Button>
                            <Button colorScheme="blue" onClick={handleSend} isLoading={sending}>
                                Send
                            </Button>
                        </HStack>
                    </ModalBody>
                </ModalContent>
            </Modal>

            <RefundModal isOpen={isRefundOpen} onClose={onRefundClose} transaction={transaction}
                isARB={transaction.arbSubscriptionId}
            />
            <VoidConfirmationModal
                isOpen={isVoidOpen}
                isARB={transaction.arbSubscriptionId}
                onClose={() => {
                    onVoidClose()
                    fetchStatus()
                }}
                transactionId={transaction.transactionId}
                onSuccess={() => load()} // re-fetch after successful void
            />

        </Box>
    );
}
